import { Document } from 'schemas/DocumentSchema'
import { Sentence } from 'schemas/SentenceSchema'
import { PostMetric } from 'schemas/PostMetricSchema'
import { SoundbiteSentimentStrings } from 'enums/SoundbiteSentiment'
import { WidgetTypeStrings } from 'enums/WidgetType'
import { TimeZoneStrings } from 'enums/TimeZone'
import { StreamData } from 'types/streamData'
import { SourceType, Domain } from 'enums'
import { DocumentMetadataAdaptor } from './soundbite/adaptor/documentMetadataAdaptor'
import { FieldResolver } from './soundbite/resolver/fieldResolver'
import {
  authorRelatedFieldsResolver,
  cleanTextRelatedFieldsResolver,
  displayTextRelatedFieldsResolver,
  documentRelatedFieldsResolver,
  highlightingRelatedFieldsResolver,
  insightCategoryRelatedFieldsResolver,
  mentionedUrlRelatedFieldsResolver,
  metricRelatedFieldsResolver,
  originalAuthorRelatedFieldsResolver,
  parentTextRelatedFieldsResolver,
  sentenceRelatedFieldsResolver,
  timeRelatedFieldsResolver,
  adsTypeResolver,
  annotatedSentimentResolver,
  isSimilarResolver,
  photoUserTagsResolver,
  postRatingResolver
} from './soundbite/resolver/index'
import { SentenceMetadataAdaptor } from './soundbite/adaptor/sentenceMetadataAdaptor'
import { DeletedDocumentMetadataAdaptor } from './soundbite/adaptor/deletedDocumentMetadataAdaptor'
import { PostMetricMetadataAdaptor } from './soundbite/adaptor/postMetricMetadataAdaptor'
import { Metadata } from './soundbite/adaptor/metadata'

const streamDataFieldResolvers: FieldResolver[] = [
  authorRelatedFieldsResolver,
  cleanTextRelatedFieldsResolver,
  displayTextRelatedFieldsResolver,
  documentRelatedFieldsResolver,
  highlightingRelatedFieldsResolver,
  insightCategoryRelatedFieldsResolver,
  mentionedUrlRelatedFieldsResolver,
  metricRelatedFieldsResolver,
  originalAuthorRelatedFieldsResolver,
  parentTextRelatedFieldsResolver,
  sentenceRelatedFieldsResolver,
  timeRelatedFieldsResolver,
  adsTypeResolver,
  annotatedSentimentResolver,
  isSimilarResolver,
  photoUserTagsResolver,
  postRatingResolver
]

const buildStreamData = (metadata: Metadata) => {
  let result = ({} as any) as StreamData
  streamDataFieldResolvers.forEach((resolver) => {
    result = { ...result, ...resolver(metadata) }
  })

  return result
}

export const getStreamDataFromDocument = (
  document: Document,
  timeZone: TimeZoneStrings,
  annotatedSentiment?: SoundbiteSentimentStrings,
  vocDisplay?: boolean,
  needTruncated?: boolean,
  truncatedLength?: number
): StreamData => {
  let adaptor

  if (document.deleted) {
    adaptor = new DeletedDocumentMetadataAdaptor(document, timeZone)
  } else {
    adaptor = new DocumentMetadataAdaptor(
      document,
      timeZone,
      annotatedSentiment,
      vocDisplay,
      needTruncated,
      truncatedLength
    )
  }

  return buildStreamData(adaptor)
}

export const getStreamDataFromDocumentAndSentence = (
  document: Document,
  sentence: Sentence,
  timeZone: TimeZoneStrings,
  annotatedSentiment?: SoundbiteSentimentStrings,
  vocDisplay?: boolean,
  needTruncated?: boolean,
  truncatedLength?: number
): StreamData => {
  if (document.deleted) {
    const adaptor = new DeletedDocumentMetadataAdaptor(document, timeZone)
    return buildStreamData(adaptor)
  }

  const documentAdaptor = new DocumentMetadataAdaptor(
    document,
    timeZone,
    annotatedSentiment,
    vocDisplay,
    needTruncated,
    truncatedLength
  )
  const sentenceAdaptor = new SentenceMetadataAdaptor(
    documentAdaptor,
    sentence,
    vocDisplay,
    needTruncated,
    truncatedLength
  )

  return buildStreamData(sentenceAdaptor)
}

export const getStreamDataFromPostMetric = (
  postMetric: PostMetric,
  timeZone: TimeZoneStrings,
  widgetType: WidgetTypeStrings,
  vocDisplay?: boolean,
  needTruncated?: boolean,
  truncatedLength?: number
) => {
  const adaptor = new PostMetricMetadataAdaptor(
    postMetric,
    timeZone,
    widgetType,
    vocDisplay,
    needTruncated,
    truncatedLength
  )

  return buildStreamData(adaptor)
}

export const isTwitterPost = (data: StreamData): boolean =>
  data.sourceType === SourceType.TWITTER || data.domain === Domain.TWITTER
